import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatLegacyButton as MatButton, MatLegacyButtonModule } from '@angular/material/legacy-button';
import { PanelDef } from 'app/shared/components/panels/panels.types';
import { Tab } from '../../../types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { MatLegacyTabsModule } from '@angular/material/legacy-tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { MatDividerModule } from '@angular/material/divider';
import { TsComponent } from 'app/modules/jupiter/@components/ts.component';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { filter, map } from 'rxjs';
import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';
import { TsButtonModule, TSIconModule } from 'tsui';
import { JupiterState } from 'app/modules/jupiter/@store/state';
import { getPanels } from 'app/modules/jupiter/functions';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { DirectivesModule } from '@shared/directives/directives.module';
import { PermissionSlot } from '@modules/jupiter/@store/types';
import { PermissionSlotPipe } from '@modules/jupiter/@pipes/permission-slot.pipe';

@Component({
  selector: 'jupiter-tab-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    MatLegacyTabsModule,
    DragDropModule,
    MatIconModule,
    MatLegacyButtonModule,
    MatLegacyMenuModule,
    FormsModule,
    ReactiveFormsModule,
    MatDividerModule,
    AsyncPipe,
    TsButtonModule,
    TSIconModule,
    MatLegacyTooltipModule,
    DirectivesModule,
    PermissionSlotPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JupiterTabHeaderComponent extends TsComponent implements OnInit {
  @Input({ required: true }) tabs: Tab[];
  @Input({ required: true }) tabId: string;
  @Input({ required: true }) editMode = false;
  @Input({ required: true }) canEditLayout = true;
  @Output() panelAdded = new EventEmitter<PanelDef<any>>();
  @Output() tabAdded = new EventEmitter<Tab>();
  @Output() tabUpdated = new EventEmitter<Tab>();
  @Output() tabRemoved = new EventEmitter<Tab>();
  @Output() tabMoved = new EventEmitter<CdkDragDrop<any[]>>();
  @Output() toggleEditMode = new EventEmitter();
  @Output() tabChange = new EventEmitter<string>();
  @Output() layoutSaved = new EventEmitter<boolean>();
  @Output() layoutCanceled = new EventEmitter<boolean>();

  customObjects$ = this.store
    .select(ObjectsManagerState.getLinkedObjects(this.store.selectSnapshot(JupiterState.getRelatedToObject)))
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      filter((objects) => !!objects),
      map((objects) => {
        const linkedObjects = objects.filter((object) => object.id);
        this.hasLinkedObjects.set(linkedObjects.length > 0);
        return linkedObjects;
      }),
    );

  PermissionSlot = PermissionSlot;
  tabForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    id: new FormControl(null),
  });
  panels: PanelDef<any>[] = [];
  hasLinkedObjects = signal(false);

  @ViewChild('tabPanel') private tabPanel: TemplateRef<any>;
  @ViewChild('tabPanelOrigin') private tabPanelOrigin: MatButton;
  private tabPanelOverlayRef: OverlayRef;
  private tabPanelTemplatePortal: TemplatePortal;

  constructor(
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
  ) {
    super();

    this.panels = getPanels(this.store.selectSnapshot(JupiterState.getFeatureSupport));
  }

  ngOnInit(): void {}

  drop(event: CdkDragDrop<any[]>): void {
    this.tabMoved.emit(event);
  }

  openTabPanel(tab?: Tab): void {
    if (tab) {
      this.tabForm.setValue({ id: tab.id, name: tab.name });
    }

    this.tabPanelOverlayRef = this.overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.tabPanelOrigin._elementRef.nativeElement)
        .withPositions([{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }]),
    });

    this.tabPanelOverlayRef
      .attachments()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.tabPanelOverlayRef.overlayElement.querySelector('input').focus());

    this.tabPanelTemplatePortal = new TemplatePortal(this.tabPanel, this.viewContainerRef);

    this.tabPanelOverlayRef.attach(this.tabPanelTemplatePortal);

    this.tabPanelOverlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closeTabPanel());
  }

  saveTab(): void {
    const tab = this.tabForm.getRawValue();
    (tab.id ? this.tabUpdated : this.tabAdded).emit(tab);
    this.closeTabPanel();
  }

  removeTab(tab: Tab): void {
    this.tabRemoved.emit(tab);
  }

  addRelatedTablePanel(customObject: CustomObject): void {
    this.panelAdded.emit({
      ...this.panels.find((panel) => panel.type === 'table'),
      meta: {
        fields: customObject.fields
          .filter((x) => x.name === 'id' || x.id === customObject.search_custom_field_id)
          .map((x) => x.name),
        tableType: customObject.name,
      },
    });
  }

  onTabClick(tabId: string): void {
    if (this.tabId !== tabId) {
      this.tabChange.emit(tabId);
    }
  }

  private closeTabPanel(): void {
    if (this.tabPanelOverlayRef?.hasAttached()) this.tabPanelOverlayRef.detach();
    if (this.tabPanelTemplatePortal?.isAttached) this.tabPanelTemplatePortal.detach();

    this.tabForm.setValue({ id: null, name: '' });
  }
}
